import * as React from 'react';
import { useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client';

import BatchTranslatePlainTextQuery from 'bundles/ondemand-translation/api/BatchTranslatePlainTextQuery.graphql';
import type {
  TranslateContent_BatchTranslatePlainTextQuery as BatchTranslatePlainTextQueryResult,
  TranslateContent_BatchTranslatePlainTextQueryVariables as BatchTranslatePlainTextQueryVariables,
} from 'bundles/ondemand-translation/api/__generated__/BatchTranslatePlainTextQuery';
import type { TranslatedContentId } from 'bundles/ondemand-translation/components/TranslationContextProvider';
import { TranslationContext } from 'bundles/ondemand-translation/components/TranslationContextProvider';

type TranslatedPlainTextProps = {
  enableTranslation?: boolean;
  plainText: string;
};

/**
 * This component gets a plain text content as an input and calls translation API to return translated content.
 *
 * @param enableTranslation specifies whether the translation feature is enabled.
 * @param plainText input content
 */
export const TranslatedPlainText = ({ enableTranslation, plainText }: TranslatedPlainTextProps) => {
  const context = React.useContext(TranslationContext);

  const contentIdRef = useRef<TranslatedContentId | undefined>(undefined);

  useEffect(() => {
    if (!contentIdRef.current) {
      contentIdRef.current = context?.getTranslatedContentId();
    }
  }, [contentIdRef, context]);

  useEffect(() => {
    if (enableTranslation && context && contentIdRef.current) {
      context.languageDetector.registerOriginalTextValue(contentIdRef.current, plainText);
    }
  }, [context, enableTranslation, plainText]);

  // Do not translate if input is empty or whitespace - backend returns validation error for empty input
  const shouldTranslate = enableTranslation && context?.showTranslatedContent && plainText && plainText.trim();

  const queryVariables = {
    inputs: [plainText],
    // Pass empty source language as the language detection is more reliable on the backend.
    languageSettings: {},
  };

  const { data, error, refetch, loading } = useQuery<
    BatchTranslatePlainTextQueryResult,
    BatchTranslatePlainTextQueryVariables
  >(BatchTranslatePlainTextQuery, {
    context: { clientName: 'gatewayGql' },
    skip: !shouldTranslate,
    variables: queryVariables,
    notifyOnNetworkStatusChange: true, // this needs to be enabled, otherwise it will not rerender on refetch
  });

  useEffect(() => {
    if (contentIdRef.current) {
      context?.progressTracker.registerQueryProgress(contentIdRef.current, loading);
    }
  }, [context?.progressTracker, loading]);

  let hasError = error !== undefined;

  let outputContent = plainText;

  const result = data?.TranslateContent?.batchTranslatePlainText;

  useEffect(() => {
    if (contentIdRef.current) {
      if (result?.__typename === 'TranslateContent_BatchTranslatePlainTextSuccess' && result.detectedSourceLanguage) {
        context?.languageDetector.registerDetectedLanguage(contentIdRef.current, result.detectedSourceLanguage);
      }
    }
  }, [context?.languageDetector, result]);

  if (result?.__typename === 'TranslateContent_BatchTranslatePlainTextSuccess') {
    const translatedData = result.results?.[0];

    if (translatedData?.__typename === 'TranslateContent_TranslatePlainTextPayload') {
      outputContent = translatedData.plainText;
    } else if (translatedData?.__typename === 'TranslateContent_TranslateFailedError') {
      hasError = true;
    }
  } else if (result?.__typename === 'TranslateContent_RateLimitError') {
    hasError = true;
  } else if (result?.__typename === 'TranslateContent_TranslateInvalidArgumentError') {
    hasError = true;
  }

  useEffect(() => {
    // Do not register / unregister error while loading (prevents flickering when refetching)
    if (contentIdRef.current && !loading) {
      if (hasError) {
        context?.progressTracker.registerError(contentIdRef.current, refetch);
      } else {
        context?.progressTracker.unregisterError(contentIdRef.current);
      }
    }
  }, [context?.progressTracker, hasError, loading, refetch]);

  return <>{outputContent}</>;
};
